import { useEffect, useState, useRef } from "react";
import { matchClock, formatInjuryTime, isMatchInProgress } from "../services/minute-a-minute/utils/clockUtils";

/**
 * Hook personalizado para sincronizar y renderizar el reloj del partido
 * IMPLEMENTACIÓN EXACTA según especificación:
 * - Mostrar solo cuando hay period_start (reloj en juego)
 * - Mostrar injuryTime cuando hay period_start y server_time
 * - El clockOffset persiste entre refreshes (no se resetea)
 * 
 * @param {Object} soccerData - Datos SoccerData del API
 * @returns {Object} { clock, injuryTime, isActive, showOnlyTitle }
 */
export const useMatchClock = (soccerData) => {
  const [clock, setClock] = useState(null);
  const [injuryTime, setInjuryTime] = useState("");
  const [isActive, setIsActive] = useState(false);
  
  const clockIntervalRef = useRef(null);
  const lastClockRef = useRef(null);

  // Limpiar intervalo al desmontar
  useEffect(() => {
    return () => {
      if (clockIntervalRef.current) {
        clearInterval(clockIntervalRef.current);
        clockIntervalRef.current = null;
      }
    };
  }, []);

  // Actualizar metadata (compensación) y determinar modo de visualización
  useEffect(() => {
    if (!soccerData?.period_id) {
      setInjuryTime("");
      setIsActive(false);
      return;
    }

    const inProgress = isMatchInProgress(soccerData.period_id);
    const hasPeriodStart = !!soccerData.period_start;
    const hasServerTime = !!soccerData.server_time;
    const isPause = soccerData.period_id >= 10 && soccerData.period_id <= 13;

    // Si es pausa (10-13) sin period_start, solo mostrar titulo (que viene desde props)
    if (isPause && !hasPeriodStart) {
      setIsActive(false);
      setInjuryTime("");
      return;
    }

    // Si no hay period_start, solo mostrar titulo (que viene desde props)
    if (!hasPeriodStart) {
      setIsActive(false);
      setInjuryTime("");
      return;
    }

    // Si tiene period_start y server_time, mostrar reloj + compensación
    if (hasPeriodStart && hasServerTime) {
      setIsActive(inProgress);
      setInjuryTime(formatInjuryTime(soccerData.announced_injury_time));
      return;
    }

    // Default
    setIsActive(false);
    setInjuryTime("");
  }, [soccerData?.period_id, soccerData?.period_start, soccerData?.server_time, soccerData?.announced_injury_time]);

  // Reloj que se actualiza cada segundo
  useEffect(() => {
    // Limpiar intervalo anterior
    if (clockIntervalRef.current) {
      clearInterval(clockIntervalRef.current);
      clockIntervalRef.current = null;
    }

    // Solo mostrar reloj si hay period_start
    if (!soccerData?.period_start) {
      setClock(null);
      return;
    }

    // Validar que sea un periodo válido
    if (!(soccerData?.period_id in { 1: 0, 2: 45, 3: 90, 4: 105 })) {
      setClock(null);
      return;
    }

    if (!isMatchInProgress(soccerData.period_id)) {
      setClock(null);
      return;
    }

    // Función que calcula y renderiza el reloj cada segundo
    const updateClock = () => {
      const newClock = matchClock(soccerData);
      
      // Solo actualizar si cambió el valor (evitar renders innecesarios)
      if (newClock !== lastClockRef.current) {
        setClock(newClock);
        lastClockRef.current = newClock;
      }
    };

    // Actualizar inmediatamente
    updateClock();

    // SEGÚN ESPECIFICACIÓN: "pintar cada segundo"
    // Crear intervalo para actualizar cada segundo
    clockIntervalRef.current = setInterval(updateClock, 1000);

    // Limpiar al cambiar dependencias
    return () => {
      if (clockIntervalRef.current) {
        clearInterval(clockIntervalRef.current);
        clockIntervalRef.current = null;
      }
    };
  }, [soccerData?.period_id, soccerData?.period_start]);

  return {
    clock,
    injuryTime,
    isActive,
  };
};

export default useMatchClock;
